import {Icon} from '@/app/components/Icon';
import {useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {Link, Outlet, useLocation, useNavigate} from 'react-router-dom';
import {Badge} from '@/components/ui/badge';
import {Button} from '@/components/app/button';
import {confirm} from '@/components/app/confirmation';
import {MenuAction} from '@/components/app/navigation';
import {RouteTabs, routeTabParts, type RouteTabOptions} from '@/components/app/navigation';
import {cn} from '@/lib/utils';
import {useAppDispatch} from '../../../app/hooks/useAppDispatch';
import {useAppSelector} from '../../../app/hooks/useAppSelector';
import {useDevice} from '../../../app/hooks/useDevice';
import {
  useGetBiobookCountsQuery,
  useGetBiobookPreferencesQuery,
  useGetBiobookQuery,
} from '../../../app/services/biobookApi';
import {
  useCheckBiotwinCalculationQuery,
  useGetBiotwinConditionsQuery,
  useGetBiotwinQuery,
  useGetRelevantConditionsQuery,
} from '../../../app/services/biotwinApi';
import {useBlockUI} from '../../core/hooks/useBlockUI';
import {usePageMessage} from '../../core/hooks/usePageMessage';
import {biotwinHub} from '../../core/hubs/biotwinHub';
import {selectSession, selectTenantId} from '../../auth/authSlice';
import {ConditionRelationType, ConditionTypeName, RelevantCondition} from '../../biotwin/types';
import {setSelectedConditionIds} from '../personSlice';
import {BiobookCount} from '../../biobook/components/BiobookCount';
import {BiotwinWizard} from '../../biotwin/components/BiotwinWizard';
import {useReadOnly} from '../../biotwin/hooks/useReadOnly';
import {useHandleBiobookUpdates} from '../hooks/useHandleBiobookUpdates';
import {useRelevantConditionsValidation} from '../../biotwin/hooks/useRelevantConditionsValidation';
import {
  contains as containsUtil,
  dedupeRelevantConditions,
  generateRelations,
  getOptionalRelatedRelevantConditions,
  getRequiredRelatedRelevantConditions,
  getSelectedRelevantConditions,
  hasRelation as hasRelationUtil,
} from '../../biotwin/utils';
import {PersonContext} from '../contexts/PersonContext';
import {useUserId} from '../hooks/useUserId';
import {isCompleteBiotwin} from '../../biotwin/helpers/utils';
import {AIChat} from '../components/AIChat';
export const Person = () => {
  const userId = useUserId();
  if (!userId) {
    throw new Error('UserId is required');
  }
  const session = useAppSelector(selectSession);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const tenantId = useAppSelector(selectTenantId);
  const refetchOnMountOrArgChange = tenantId !== 1;
  const [activeIndex, setActiveIndex] = useState(0);
  const {isFetching: isFetchingCheckBiotwinCalculation, isLoading: isLoadingCheckBiotwinCalculation} =
    useCheckBiotwinCalculationQuery(
      {
        id: userId,
      },
      {
        refetchOnMountOrArgChange,
      },
    );
  const {
    data: biotwin,
    isLoading: isLoadingBiotwin,
    isUninitialized: isUninitializedBiotwin,
  } = useGetBiotwinQuery({
    id: userId,
  });
  const {data: biotwinConditions = [], isFetching: isFetchingBiotwinConditions} = useGetBiotwinConditionsQuery(
    {
      userId,
    },
    {
      refetchOnMountOrArgChange,
      skip: isLoadingCheckBiotwinCalculation,
    },
  );
  const {data: relevantConditions, isFetching: isFetchingRelevantConditions} = useGetRelevantConditionsQuery(
    {
      userId,
    },
    {
      refetchOnMountOrArgChange,
      skip: isLoadingCheckBiotwinCalculation,
    },
  );
  const {data: biobook, isFetching: isFetchingBiobook} = useGetBiobookQuery(
    {
      userId,
    },
    {
      refetchOnMountOrArgChange,
      skip: isLoadingCheckBiotwinCalculation || activeIndex !== 1,
    },
  );
  const {data: biobookCounts, isFetching: isFetchingBiobookCounts} = useGetBiobookCountsQuery(
    {
      userId,
    },
    {
      refetchOnMountOrArgChange,
      skip: isLoadingCheckBiotwinCalculation || activeIndex === 1,
    },
  );
  const {data: biobookPreferences, isFetching: isFetchingBiobookPreferences} = useGetBiobookPreferencesQuery(
    {
      userId,
    },
    {
      refetchOnMountOrArgChange,
      skip: isLoadingCheckBiotwinCalculation,
    },
  );
  const [biotwinWizardVisible, setBiotwinWizardVisible] = useState<boolean>();
  const {isReadOnly} = useReadOnly();
  const {isMobile, isLandscape} = useDevice();
  const readOnly = isReadOnly(biotwin);
  const biotwinHubContext = useContext(biotwinHub.Context);
  const {
    relations,
    relationsByConditionId,
    relevantConditionsDeduplicated,
    relevantConditionsOptionalRelated,
    relevantConditionsRequiredRelated,
    relevantConditionsSelected,
  } = useMemo(() => {
    const relevantConditionsDeduplicated = dedupeRelevantConditions(relevantConditions ?? []);
    const relevantConditionsOptionalRelated = getOptionalRelatedRelevantConditions(relevantConditionsDeduplicated);
    const relevantConditionsRequiredRelated = getRequiredRelatedRelevantConditions(relevantConditionsDeduplicated);
    const relevantConditionsSelected = getSelectedRelevantConditions(relevantConditionsDeduplicated);
    const relations = generateRelations(relevantConditionsDeduplicated);
    const relationsByConditionId = Object.fromEntries(relations.map((r) => [r.relevantCondition.conditionId, r]));
    return {
      relations,
      relationsByConditionId,
      relevantConditionsDeduplicated,
      relevantConditionsOptionalRelated,
      relevantConditionsRequiredRelated,
      relevantConditionsSelected,
    };
  }, [relevantConditions]);
  const relatedCache: Map<string, boolean> = useMemo(() => {
    return new Map();
  }, [relevantConditions]);
  const hasRelation = useCallback(
    (conditionId1: number, conditionId2: number) => {
      let result = relatedCache.get(`${conditionId1}-${conditionId2}`);
      if (result == null) {
        result = hasRelationUtil(
          relationsByConditionId[conditionId1].relevantCondition,
          relationsByConditionId[conditionId2].relevantCondition,
          relationsByConditionId,
        );
        relatedCache.set(`${conditionId1}-${conditionId2}`, result);
      }
      return result;
    },
    [relevantConditions, relatedCache, relationsByConditionId],
  );
  const containsCache: Record<string, boolean> = useMemo(() => {
    return {};
  }, [relevantConditions]);
  const contains = useCallback(
    (conditionId1: number, conditionId2: number) => {
      let result = containsCache[`${conditionId1}-${conditionId2}`];
      if (result == null) {
        result = containsUtil(
          relationsByConditionId[conditionId1].relevantCondition,
          relationsByConditionId[conditionId2].relevantCondition,
          relationsByConditionId,
        );
        containsCache[`${conditionId1}-${conditionId2}`] = result;
      }
      return result;
    },
    [relevantConditions, containsCache, relationsByConditionId],
  );
  const isOptionalRelated = useCallback(
    (arg: number | RelevantCondition) =>
      typeof arg === 'number'
        ? relevantConditionsOptionalRelated.some((rc) => rc.conditionId === arg)
        : relevantConditionsOptionalRelated.includes(arg),
    [relevantConditionsOptionalRelated],
  );
  const isRequiredRelated = useCallback(
    (arg: number | RelevantCondition) =>
      typeof arg === 'number'
        ? relevantConditionsRequiredRelated.some((rc) => rc.conditionId === arg)
        : relevantConditionsRequiredRelated.includes(arg),
    [relevantConditionsRequiredRelated],
  );
  const isSelected = useCallback(
    (arg: number | RelevantCondition) =>
      typeof arg === 'number'
        ? relevantConditionsSelected.some((rc) => rc.conditionId === arg)
        : relevantConditionsSelected.includes(arg),
    [relevantConditionsSelected],
  );
  const isSuggested = useCallback(
    (arg: number | RelevantCondition) =>
      typeof arg === 'number'
        ? relevantConditionsDeduplicated.some((rc) => rc.conditionId === arg)
        : relevantConditionsDeduplicated.includes(arg),
    [relevantConditionsDeduplicated],
  );
  useBlockUI(isFetchingBiotwinConditions || isFetchingCheckBiotwinCalculation || isFetchingRelevantConditions);
  useEffect(() => {
    if (biotwinHubContext?.connection?.state === 'Connected') {
      const connection = biotwinHubContext?.connection;
      void connection.send('AddUser', userId);
      return () => {
        void connection.send('RemoveUser', userId);
      };
    }
  }, [userId, biotwinHubContext?.connection?.state]);
  useHandleBiobookUpdates(userId);
  useRelevantConditionsValidation(relevantConditions ?? [], {
    consoleLogEnabled: session?.isAdmin === 'true',
    pageMessageEnabled: session?.isAdmin === 'true',
  });
  useEffect(() => {
    if (!location.pathname.includes('/biobook')) {
      dispatch(setSelectedConditionIds([]));
    }
  }, [dispatch, location.pathname]);
  useLayoutEffect(() => {
    setActiveIndex(
      ['biotwin', 'biobook', 'bookmarks', 'updates'].findIndex((value) => location.pathname.endsWith(value)),
    );
  }, [location.pathname]);
  usePageMessage(
    {
      dismissable: isMobile && isLandscape,
      severity: 'warn',
      content: (
        <span>
          DISCLAIMER: THIS WEBSITE DOES NOT PROVIDE MEDICAL ADVICE&nbsp;
          <Button
            className="h-auto p-0 font-semibold text-inherit underline"
            variant="link"
            onClick={() => {
              confirm({
                confirmLabel: 'Close',
                className: 'sm:max-w-lg',
                dismissible: true,
                title: (
                  <span>
                    <Icon icon="exclamation-triangle" className="text-2xl" /> DISCLAIMER
                  </span>
                ),
                message:
                  'The information, including but not limited to, text, graphics, images and other material contained on this website are for informational purposes only. No material on this site is intended to be a substitute for professional medical advice, diagnosis or treatment. Always seek the advice of your physician or other qualified health care provider with any questions you may have regarding a medical condition or treatment and before undertaking a new health care regimen, and never disregard professional medical advice or delay in seeking it because of something you have read on this website.',
                showCancel: false,
              });
            }}
            type="button"
          >
            Click for details.
          </Button>
        </span>
      ),
    },
    [],
    session?.isAdmin !== 'true',
  );
  const biobookCountSource = activeIndex === 1 ? 'biobook' : 'biobookCounts';
  if (!session) {
    return null;
  }
  if (!isUninitializedBiotwin && !isLoadingBiotwin && !isCompleteBiotwin(biotwin) && biotwinWizardVisible == null) {
    setBiotwinWizardVisible(true);
    return null;
  }
  return (
    <PersonContext.Provider
      value={{
        biotwinConditions,
        contains,
        hasRelation,
        isOptionalRelated,
        isRequiredRelated,
        isSelected,
        isSuggested,
        relations,
        relationsByConditionId,
        relevantConditions,
        relevantConditionsDeduplicated,
        relevantConditionsOptionalRelated,
        relevantConditionsRequiredRelated,
        relevantConditionsSelected,
        userId,
      }}
    >
      <div className="page-container p-0 pt-4 flex flex-col sm:p-4 sm:pt-6 md:h-full">
        <RouteTabs
          aria-label="Biotwin views"
          activeIndex={activeIndex}
          items={[
            {
              icon: 'tag',
              label: 'Biotwin',
              template: (item, options) => {
                const conditionCount =
                  relevantConditions?.reduce(
                    (total, rc) => {
                      if (rc.conditionTypeName === ConditionTypeName.Demographic) {
                        return total;
                      }
                      if (rc.isPatientSetting) {
                        return total;
                      }
                      if (rc.conditionRelationType === ConditionRelationType.Selected) {
                        return total + 1;
                      }
                      if (rc.conditionRelationType === ConditionRelationType.RequiredRelated) {
                        const phraseCount =
                          biobookCounts?.conditionPhraseCounts.find(({conditionId}) => conditionId === rc.conditionId)
                            ?.phraseCount ?? 0;
                        if (phraseCount > 0 || !rc.hideWhenNoPhrase) {
                          return total + 1;
                        }
                      }
                      return total;
                    },
                    isCompleteBiotwin(biotwin) ? 4 : 0,
                  ) ?? 0;
                return (
                  <TabMenuItemLink
                    {...options}
                    badge={conditionCount}
                    isLoading={isLoadingCheckBiotwinCalculation || isFetchingRelevantConditions}
                    item={item}
                    to={'biotwin'}
                  />
                );
              },
            },
            {
              icon: 'book',
              label: 'Biobook',
              template: (item, options) => (
                <TabMenuItemLink
                  {...options}
                  badge={biobookCountSource === 'biobook' ? <BiobookCount /> : biobookCounts?.phraseCount}
                  isLoading={
                    isLoadingCheckBiotwinCalculation ||
                    (biobookCountSource === 'biobook' && isFetchingBiobook) ||
                    (biobookCountSource === 'biobookCounts' && isFetchingBiobookCounts)
                  }
                  item={item}
                  to={'biobook'}
                />
              ),
            },
            {
              icon: 'bookmark',
              label: 'Bookmarks',
              template: (item, options) => (
                <TabMenuItemLink
                  {...options}
                  badge={readOnly ? '-' : biobookPreferences?.favoritePhraseIds.length}
                  isLoading={isLoadingCheckBiotwinCalculation || isFetchingBiobookPreferences}
                  item={item}
                  to={'bookmarks'}
                />
              ),
            },
            {
              icon: 'file-earmark-plus-fill',
              label: 'Updates',
              template: (item, options) => (
                <TabMenuItemLink
                  {...options}
                  badge={readOnly ? '-' : (biotwin?.biobookUpdatePhraseIds?.length ?? 0)}
                  isLoading={isLoadingCheckBiotwinCalculation || isLoadingBiotwin}
                  item={item}
                  to={'updates'}
                />
              ),
            },
          ]}
          onActiveChange={(e) => setActiveIndex(e)}
        />
        <div className="flex-1 min-h-0 overflow-auto bg-white sm:shadow-md sm:rounded-r-xl sm:rounded-b-xl relative">
          {!isLoadingCheckBiotwinCalculation && <Outlet />}
        </div>
      </div>

      <AIChat userId={userId} />

      {biotwin != null && (
        <BiotwinWizard
          initialBiotwin={biotwin}
          initialStep={3}
          noNewBiotwin
          noViewBiotwin
          onCancel={() => navigate('/')}
          onFinish={() => setBiotwinWizardVisible(false)}
          onViewBiotwin={() => {}}
          startingStep={3}
          summaryStep={3}
          tenantId={tenantId}
          visible={biotwinWizardVisible}
        />
      )}
    </PersonContext.Provider>
  );
};
interface TabMenuItemLinkProps extends RouteTabOptions {
  badge?: React.ReactNode;
  isLoading: boolean;
  item: Pick<MenuAction, 'icon' | 'label'>;
  to: string;
}
const TabMenuItemLink = ({badge, isLoading, item, to, active, className, onSelect}: TabMenuItemLinkProps) => {
  return (
    <Link
      to={to}
      className={className}
      data-active={active}
      aria-current={active ? 'page' : undefined}
      aria-label={item.label}
      onClick={onSelect}
    >
      <Icon icon={item.icon} className={routeTabParts.icon} />
      <span className={routeTabParts.label}>{item.label}</span>
      <Badge className={cn('h-5 w-8 rounded-3xl px-0', routeTabParts.badge)} aria-busy={isLoading || undefined}>
        {isLoading ? <Icon icon="spinner" spin /> : badge}
      </Badge>
    </Link>
  );
};
